/**
 * Per-site food & drink menu shown on the Menu page. Mirrors the shape
 * produced by wedding-upload-be (functions/src/menu.ts) and the admin editor.
 * Keep the icon + dietary keys in sync across all three.
 */
export type MenuIconKey =
  | "welcome-drink"
  | "starter"
  | "soup"
  | "salad"
  | "bread"
  | "fish"
  | "meat"
  | "pizza"
  | "vegetarian"
  | "dessert"
  | "cake"
  | "fruit"
  | "coffee"
  | "wine"
  | "beer"
  | "buffet"
  | "utensils";

export type DietaryTag =
  | "vegetarian"
  | "vegan"
  | "glutenFree"
  | "lactoseFree"
  | "nutFree"
  | "spicy";

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  dietary?: DietaryTag[];
}

export interface MenuGroup {
  id: string;
  name: string;
  /** Optional free-form time label (e.g. "from 6pm"). */
  time?: string;
  icon: MenuIconKey;
  items: MenuItem[];
}

export interface MenuConfig {
  groups: MenuGroup[];
}

/**
 * Defensively normalizes any stored menu into the canonical shape, keeping the
 * operator's group/item order (a menu is deliberately ordered, not sorted).
 */
export function normalizeMenu(raw: unknown): MenuConfig {
  if (!raw || typeof raw !== "object") return { groups: [] };
  const obj = raw as Record<string, unknown>;
  const groups = Array.isArray(obj.groups) ? (obj.groups as MenuGroup[]) : [];
  return {
    groups: groups.map((g) => ({
      ...g,
      items: Array.isArray(g.items) ? g.items : [],
    })),
  };
}

/** Whether a stored menu has at least one item. */
export function hasMenuItems(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const obj = raw as Record<string, unknown>;
  if (!Array.isArray(obj.groups)) return false;
  return (obj.groups as MenuGroup[]).some(
    (g) => Array.isArray(g.items) && g.items.length > 0,
  );
}
